import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { format } from 'date-fns';
import { Timestamp, doc, updateDoc, arrayUnion, arrayRemove, collection, query, where, getDocs, addDoc } from 'firebase/firestore';
import { FolderPhotosCard } from '../claire/FolderPhotosCard';
import { FolderListCard } from '../claire/FolderListCard';
import { ClaireText } from '../claire/ClaireText';
import { db } from '../../lib/firebase/config';
import { Check, CheckCheck, Edit3, X, Save, Download, FileText, Image, Video, File, ThumbsUp, ThumbsDown, Heart, Laugh, Frown, PartyPopper, Angry, Smile, SmilePlus, Reply, CornerDownRight } from 'lucide-react';
import { ChatAttachment } from '../../services/chat';
import { readReceiptsService } from '../../services/readReceiptsService';

interface ReplyTo {
  messageId: string;
  text: string;
  sender: string;
  senderName?: string;
}

interface MessageBubbleProps {
  message: {
    id: string;
    text: string;
    sender: string;
    imageUrl?: string;
    videoUrl?: string;
    attachment?: ChatAttachment;
    timestamp: Timestamp;
    editedAt?: Timestamp;
    isEdited?: boolean;
    isSystemMessage?: boolean;
    replyTo?: ReplyTo;
    /** One-tap answers Claire offers (Send to Claire). Hidden once someone answers. */
    claireOptions?: string[];
    claireAnswered?: boolean;
    /** Claire's photo card for adding folder photos to product entries. */
    folderJobId?: string;
    /** Claire's card for reading the folder names in the photo folder. */
    folderListJobId?: string;
    /** Posted by Claire's account; its text is her light Markdown. */
    fromClaire?: boolean;
    reactions?: {
      thumbsUp?: string[];
      thumbsDown?: string[];
      heart?: string[];
      laugh?: string[];
      haha?: string[];
      sad?: string[];
      party?: string[];
      angry?: string[];
    };
  };
  isOwn: boolean;
  showSender?: boolean;
  showTimestamp?: boolean;
  senderDisplayName?: string;
  senderPhotoURL?: string;
  chatId: string;
  currentUserEmail?: string;
  compact?: boolean;
  onEditMessage?: (messageId: string, newText: string) => void;
  onReplyMessage?: (message: any) => void;
  getDisplayName?: (email: string) => string;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  message,
  isOwn,
  showSender = false,
  showTimestamp = false,
  senderDisplayName,
  senderPhotoURL,
  chatId,
  currentUserEmail,
  compact = false,
  onEditMessage,
  onReplyMessage,
  getDisplayName,
}) => {
  const [quickReplySent, setQuickReplySent] = useState(false);
  const sendQuickReply = async (text: string) => {
    if (!currentUserEmail || quickReplySent) return;
    setQuickReplySent(true);
    try {
      await addDoc(collection(db, 'chats', chatId, 'messages'), {
        sender: currentUserEmail,
        text,
        timestamp: Timestamp.now(),
        replyTo: {
          messageId: message.id,
          text: message.text?.substring(0, 100) || '',
          sender: message.sender,
          senderName: getDisplayName ? getDisplayName(message.sender) : message.sender,
        },
      });
      await updateDoc(doc(db, 'chats', chatId), { lastMessage: text, lastMessageSender: currentUserEmail, updatedAt: Timestamp.now() });
    } catch (error) {
      console.error('Error sending quick reply:', error);
      setQuickReplySent(false);
    }
  };
  const [readReceipts, setReadReceipts] = useState<Array<{ userId: string; readAt: Timestamp }>>([]);
  const [isRead, setIsRead] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState(message.text || '');
  const [showReactionTooltip, setShowReactionTooltip] = useState(false);
  const [showReactionPicker, setShowReactionPicker] = useState(false);
  const [activeReactionTooltip, setActiveReactionTooltip] = useState<string | null>(null);
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  const [tooltipPosition, setTooltipPosition] = useState<{ x: number; y: number } | null>(null);
  const reactionRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  
  // Update editText when message changes
  useEffect(() => {
    setEditText(message.text || '');
  }, [message.text]);
  
  const formatTime = (timestamp: Timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp.seconds * 1000);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return format(date, 'h:mm a');
    }
    return format(date, 'MMM d, h:mm a');
  };

  const handleStartEdit = () => {
    setIsEditing(true);
    setEditText(message.text || '');
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditText(message.text || '');
  };

  const handleSaveEdit = () => {
    if (editText.trim() !== message.text && onEditMessage) {
      onEditMessage(message.id, editText.trim());
    }
    setIsEditing(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  // Fetch display names for users who reacted
  const fetchUserDisplayName = async (email: string): Promise<string> => {
    if (userDisplayNames.has(email)) {
      return userDisplayNames.get(email)!;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', email));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const userData = querySnapshot.docs[0].data();
        const displayName = userData.displayName || email;
        setUserDisplayNames(prev => new Map(prev).set(email, displayName));
        return displayName;
      }
    } catch (error) {
      console.error('Error fetching user display name:', error);
    }
    
    // Fallback to email
    setUserDisplayNames(prev => new Map(prev).set(email, email));
    return email;
  };

  // Reaction types configuration
  const reactionTypes = [
    { type: 'thumbsUp', icon: ThumbsUp, label: '👍 Thumbs Up', color: 'blue' },
    { type: 'thumbsDown', icon: ThumbsDown, label: '👎 Thumbs Down', color: 'red' },
    { type: 'heart', icon: Heart, label: '❤️ Heart', color: 'pink' },
    { type: 'laugh', icon: Laugh, label: '😂 Laugh', color: 'yellow' },
    { type: 'haha', icon: SmilePlus, label: '😆 HaHa', color: 'green' },
    { type: 'sad', icon: Frown, label: '😢 Sad', color: 'gray' },
    { type: 'party', icon: PartyPopper, label: '🎉 Party', color: 'purple' },
    { type: 'angry', icon: Angry, label: '😠 Angry', color: 'orange' },
  ];

  // Generic reaction handler
  const handleReaction = async (reactionType: string) => {
    if (!currentUserEmail) return;

    try {
      const messageRef = doc(db, 'chats', chatId, 'messages', message.id);
      const currentReactions = (message.reactions as any)?.[reactionType] || [];
      const hasReacted = currentReactions.includes(currentUserEmail);

      if (hasReacted) {
        // Remove reaction
        await updateDoc(messageRef, {
          [`reactions.${reactionType}`]: arrayRemove(currentUserEmail)
        });
      } else {
        // Add reaction
        await updateDoc(messageRef, {
          [`reactions.${reactionType}`]: arrayUnion(currentUserEmail)
        });
      }
      
      setShowReactionPicker(false);
    } catch (error) {
      console.error('Error updating reaction:', error);
    }
  };

  // Handle thumbs up (quick action)
  const handleThumbsUp = () => handleReaction('thumbsUp');

  // Load display names for users who reacted
  useEffect(() => {
    const loadReactionUserNames = async () => {
      const allReactedUsers = new Set<string>();
      
      // Collect all users who reacted with any reaction type
      Object.values(message.reactions || {}).forEach((users: any) => {
        if (Array.isArray(users)) {
          users.forEach(email => allReactedUsers.add(email));
        }
      });

      if (allReactedUsers.size === 0) return;

      const fetchPromises = Array.from(allReactedUsers)
        .filter(email => !userDisplayNames.has(email))
        .map(email => fetchUserDisplayName(email));
      
      await Promise.allSettled(fetchPromises);
    };

    loadReactionUserNames();
  }, [message.reactions]);

  // Close reaction picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowReactionPicker(false);
      }
    };

    if (showReactionPicker) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showReactionPicker]);

  // Format file size for display
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Get file icon based on file type
  const getFileIcon = (fileType: string) => {
    if (fileType.includes('pdf')) return <FileText className="h-5 w-5 text-red-400" />;
    if (fileType.includes('image')) return <Image className="h-5 w-5 text-green-400" />;
    if (fileType.includes('video')) return <Video className="h-5 w-5 text-blue-400" />;
    return <File className="h-5 w-5 text-gray-400" />;
  };

  // Render file attachment
  const renderAttachment = (attachment: ChatAttachment, isOwn: boolean) => {
    return (
      <div className="p-3 rounded-lg border transition-colors bg-black/5 border-[#E2D2AC] hover:bg-black/10">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0">
            {getFileIcon(attachment.fileType)}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate text-[#3D2B1F]">
              {attachment.fileName}
            </p>
            <p className="text-xs text-[#6B4F3A]">
              {formatFileSize(attachment.fileSize)} • {attachment.fileType}
            </p>
          </div>
          <a
            href={attachment.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-shrink-0 p-2 rounded-lg transition-colors group bg-[#E7D9B8] hover:bg-[#DCC79B]"
            title="Download file"
          >
            <Download className="h-4 w-4 text-[#7A5A34]" />
          </a>
        </div>
      </div>
    );
  };

  // Load read receipts for this message
  useEffect(() => {
    const loadReadReceipts = async () => {
      if (!isOwn) return; // Only show read receipts for own messages
      
      try {
        const receipts = await readReceiptsService.getMessageReadReceipts(chatId, message.id);
        setReadReceipts(receipts);
        
        // Check if current user has read this message (for messages from others)
        if (currentUserEmail && !isOwn) {
          const userHasRead = receipts.some(receipt => receipt.userId === currentUserEmail);
          setIsRead(userHasRead);
        }
      } catch (error) {
        console.error('Error loading read receipts:', error);
      }
    };

    loadReadReceipts();
  }, [chatId, message.id, isOwn, currentUserEmail]);



  // Debug logging for message content
  if (message.imageUrl || message.videoUrl) {
    console.log('🎨 MessageBubble received media:', {
      imageUrl: message.imageUrl,
      videoUrl: message.videoUrl,
      text: message.text?.substring(0, 50) + '...',
      sender: message.sender
    });
  }

  // Render system messages differently (centered, simple styling)
  if (message.isSystemMessage || message.sender === 'system') {
    return (
      <div className="flex justify-center my-3">
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-full text-gray-600 text-sm">
          <span className="text-gray-400">•</span>
          <span>{message.text}</span>
          <span className="text-gray-400">•</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`group flex ${isOwn ? 'flex-row-reverse' : 'flex-row'} items-end gap-1.5 max-w-[85%] ${isOwn ? 'ml-auto' : 'mr-auto'}`}>
      {/* Profile picture for other users */}
      {!isOwn && !compact && (
        <div className="flex-shrink-0 mb-1">
          <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200">
            {senderPhotoURL ? (
              <img
                src={senderPhotoURL}
                alt={senderDisplayName || message.sender}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  const nextElement = e.currentTarget.nextElementSibling as HTMLElement;
                  if (nextElement) {
                    nextElement.style.display = 'flex';
                  }
                }}
              />
            ) : null}
            <div
              className={`w-full h-full bg-[#EFE3C4] flex items-center justify-center text-[#3D2B1F] font-bold text-sm ${
                senderPhotoURL ? 'hidden' : 'flex'
              }`}
            >
              {(senderDisplayName || message.sender).charAt(0).toUpperCase()}
            </div>
          </div>
        </div>
      )}

      {/* Message bubble container with reactions */}
      <div className={compact ? 'relative min-w-0' : 'relative flex-1 min-w-0'}>
        {/* Reaction buttons - TOP LEFT of bubble */}
        {currentUserEmail && (
          <div className={compact
            ? `absolute ${isOwn ? 'right-full mr-1' : 'left-full ml-1'} top-0 flex flex-row gap-0.5 z-10`
            : `absolute ${isOwn ? 'right-full mr-1' : 'left-0 -translate-x-full -ml-1'} top-0 flex flex-col gap-1 z-10`}>
          {/* Reply button */}
          {onReplyMessage && (
            <button
              onClick={() => onReplyMessage(message)}
              className="p-1.5 rounded-full transition-all bg-gray-100 hover:bg-teal-100 text-gray-400 hover:text-teal-600 opacity-0 group-hover:opacity-100"
              title="Reply to this message"
            >
              <Reply size={14} />
            </button>
          )}

          {/* Quick thumbs up button */}
          <button
            onClick={handleThumbsUp}
            className={`p-1.5 rounded-full transition-all ${
              message.reactions?.thumbsUp?.includes(currentUserEmail)
                ? 'bg-blue-100 text-blue-600 hover:bg-blue-200 opacity-100'
                : 'bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100'
            }`}
            title={message.reactions?.thumbsUp?.includes(currentUserEmail) ? 'Remove thumbs up' : 'Thumbs up'}
          >
            <ThumbsUp size={14} />
          </button>

          {/* More reactions button */}
          <button
            onClick={() => setShowReactionPicker(!showReactionPicker)}
            className="p-1.5 rounded-full transition-all bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100"
            title="More reactions"
          >
            <Smile size={14} />
          </button>

          {/* Reaction picker dropdown */}
          {showReactionPicker && (
            <div
              ref={pickerRef}
              className={`absolute ${isOwn ? 'right-full mr-2' : 'left-full ml-2'} top-0 bg-white border border-gray-200 rounded-lg shadow-xl p-2 flex flex-col gap-1 z-50`}
            >
              {reactionTypes.map((reaction) => {
                const Icon = reaction.icon;
                const hasReacted = (message.reactions as any)?.[reaction.type]?.includes(currentUserEmail);
              const colorClasses = {
                blue: 'hover:bg-blue-100 text-blue-600',
                red: 'hover:bg-red-100 text-red-600',
                pink: 'hover:bg-pink-100 text-pink-600',
                yellow: 'hover:bg-yellow-100 text-yellow-600',
                green: 'hover:bg-green-100 text-green-600',
                gray: 'hover:bg-gray-100 text-gray-600',
                purple: 'hover:bg-purple-100 text-purple-600',
                orange: 'hover:bg-orange-100 text-orange-600',
              };
                
                return (
                  <button
                    key={reaction.type}
                    onClick={() => handleReaction(reaction.type)}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                      hasReacted
                        ? `bg-${reaction.color}-100 ${colorClasses[reaction.color as keyof typeof colorClasses]}`
                        : `hover:bg-gray-100 text-gray-700 hover:text-gray-900`
                    }`}
                    title={reaction.label}
                  >
                    <Icon size={16} />
                    <span className="text-sm whitespace-nowrap">{reaction.label}</span>
                  </button>
                );
              })}
            </div>
          )}
          </div>
        )}

        {/* Message content */}
        <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {/* Sender name */}
        {showSender && !isOwn && (
          <span className="text-xs text-gray-500 ml-2 mb-1">
            {senderDisplayName || message.sender}
          </span>
        )}

        {/* Message bubble */}
        <div
          className={compact
            ? `max-w-[260px] rounded-[18px] text-[13.5px] leading-[1.4] overflow-hidden ${
                isOwn ? 'bg-[#efe6d2] text-[#241813]' : 'bg-white border border-gray-200 text-gray-900'
              }`
            : `max-w-[500px] min-w-[100px] rounded-2xl text-sm overflow-hidden
            ${isOwn
              ? 'bg-[#F6EFDC] border border-[#EADFC1] text-[#3D2B1F] shadow-sm'
              : 'bg-[#FDFAF2] border border-[#EFE4CC] text-[#3D2B1F] shadow-sm'
            }
          `}
        >
        {/* Reply Quote - Show the message being replied to */}
        {message.replyTo && (
          <div 
            className="px-3 py-2 border-l-3 bg-black/5 border-l-[#A9834E]"
            style={{ borderLeftWidth: '3px' }}
          >
            <div className="flex items-center gap-1.5 mb-0.5">
              <CornerDownRight className="w-3 h-3 text-[#8A6D3B]" />
              <span className="text-xs font-medium text-[#7A5A34]">
                {message.replyTo.senderName || (getDisplayName ? getDisplayName(message.replyTo.sender) : message.replyTo.sender)}
              </span>
            </div>
            <p className="text-xs truncate text-[#6B4F3A]">
              {message.replyTo.text}
            </p>
          </div>
        )}
        
        {/* Image */}
        {message.imageUrl && (
          <div className="relative">
            <img 
              src={message.imageUrl} 
              alt="Shared image" 
              className="w-full max-w-[300px] h-auto cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => window.open(message.imageUrl, '_blank')}
            />
          </div>
        )}

        {/* Video */}
        {message.videoUrl && (
          <div className="relative">
            <video 
              src={message.videoUrl} 
              controls
              className="w-full max-w-[300px] h-auto"
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
          </div>
        )}
        
        {/* Text */}
        {message.text && (
          <div className={compact ? 'px-3 py-1.5' : 'px-4 py-2'}>
            {isEditing ? (
              <div className="space-y-2">
                <textarea
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={handleKeyPress}
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
                  placeholder="Edit your message..."
                  rows={Math.min(Math.max(editText.split('\n').length, 1), 6)}
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSaveEdit}
                    className="flex items-center gap-1 px-2 py-1 bg-teal-500 hover:bg-teal-600 rounded text-xs text-white transition-colors"
                  >
                    <Save size={12} />
                    Save
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="flex items-center gap-1 px-2 py-1 bg-gray-200 hover:bg-gray-300 rounded text-xs text-gray-700 transition-colors"
                  >
                    <X size={12} />
                    Cancel
                  </button>
                  <span className="text-xs text-gray-500">
                    Press Enter to save, Esc to cancel
                  </span>
                </div>
              </div>
            ) : (
              <>
                <div className="group/message relative">
                  {message.fromClaire ? (
                    <div className="break-words" style={{ overflowWrap: 'anywhere' }}>
                      <ClaireText text={message.text} />
                    </div>
                  ) : (
                  <p 
                    className="whitespace-pre-wrap break-words overflow-wrap-anywhere"
                    style={{ 
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      hyphens: 'auto'
                    }}
                  >
                    {message.text}
                  </p>
                  )}
                  
                  {/* Edit button for own messages */}
                  {isOwn && onEditMessage && !message.imageUrl && !message.videoUrl && !message.attachment && (
                    <button
                      onClick={handleStartEdit}
                      className="absolute -top-1 -right-1 opacity-0 group-hover/message:opacity-100 transition-opacity p-1 bg-black/5 hover:bg-black/10 rounded text-[#6B4F3A] hover:text-[#3D2B1F]"
                      title="Edit message"
                    >
                      <Edit3 size={12} />
                    </button>
                  )}
                </div>
                {message.isEdited && (
                  <span className="text-xs italic text-[#6B4F3A]/80">
                    (edited{message.editedAt ? ` ${formatTime(message.editedAt)}` : ''})
                  </span>
                )}
              </>
            )}
          </div>
        )}
        
        {/* File Attachment - renders whether or not there is accompanying text */}
        {message.attachment && (
          <div className={message.text ? 'px-4 pb-2 -mt-1' : 'px-4 py-2'}>
            {renderAttachment(message.attachment, isOwn)}
          </div>
        )}

        {!isOwn && !!message.claireOptions?.length && !message.claireAnswered && (
          <div className="px-4 pb-3 flex flex-wrap gap-1.5">
            {message.claireOptions.map((option) => (
              <button
                key={option}
                type="button"
                disabled={quickReplySent}
                onClick={() => sendQuickReply(option)}
                className="rounded-full border border-[#b8986e] bg-white px-3 py-1 text-[12px] font-medium text-[#241813] hover:bg-[#faf6ec] disabled:opacity-50"
              >
                {option}
              </button>
            ))}
          </div>
        )}

        {!isOwn && message.folderJobId && (
          <div className="px-3 pb-3 min-w-[320px]">
            <FolderPhotosCard jobId={message.folderJobId} compact />
          </div>
        )}

        {!isOwn && message.folderListJobId && (
          <div className="px-3 pb-3 min-w-[320px]">
            <FolderListCard jobId={message.folderListJobId} compact />
          </div>
        )}

        {/* If only media, add minimal padding */}
        {!message.text && (message.imageUrl || message.videoUrl) && (
          <div className="h-0"></div>
        )}
        
        {/* Reactions display - show all reactions */}
        {message.reactions && Object.keys(message.reactions).length > 0 && (
          <div className="px-2 pb-2 flex flex-wrap gap-1">
            {reactionTypes.map((reactionConfig) => {
              const reactions = (message.reactions as any)?.[reactionConfig.type];
              if (!reactions || reactions.length === 0) return null;

              const Icon = reactionConfig.icon;
              const colorClasses = {
                blue: 'bg-blue-100 text-blue-600 hover:bg-blue-200',
                red: 'bg-red-100 text-red-600 hover:bg-red-200',
                pink: 'bg-pink-100 text-pink-600 hover:bg-pink-200',
                yellow: 'bg-yellow-100 text-yellow-600 hover:bg-yellow-200',
                green: 'bg-green-100 text-green-600 hover:bg-green-200',
                gray: 'bg-gray-100 text-gray-600 hover:bg-gray-200',
                purple: 'bg-purple-100 text-purple-600 hover:bg-purple-200',
                orange: 'bg-orange-100 text-orange-600 hover:bg-orange-200',
              };

              return (
                <div
                  key={reactionConfig.type}
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-1 cursor-pointer transition-colors ${colorClasses[reactionConfig.color as keyof typeof colorClasses]}`}
                  onMouseEnter={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setTooltipPosition({
                      x: rect.left + rect.width / 2,
                      y: rect.top - 10
                    });
                    setActiveReactionTooltip(reactionConfig.type);
                  }}
                  onMouseLeave={() => {
                    setActiveReactionTooltip(null);
                    setTooltipPosition(null);
                  }}
                >
                  <Icon size={12} />
                  <span className="text-xs font-medium">
                    {reactions.length}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Portal tooltip for reactions */}
        {activeReactionTooltip && tooltipPosition && createPortal(
          <div 
            className="fixed pointer-events-none"
            style={{ 
              left: tooltipPosition.x,
              top: tooltipPosition.y,
              transform: 'translateX(-50%)',
              zIndex: 999999 
            }}
          >
            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap border border-gray-700 shadow-xl">
              <div className="font-medium mb-1">
                {reactionTypes.find(r => r.type === activeReactionTooltip)?.label || 'Reaction'} from:
              </div>
              {((message.reactions as any)?.[activeReactionTooltip] || []).map((email: string, index: number) => (
                <div key={email} className="text-gray-300">
                  {userDisplayNames.get(email) || email}
                </div>
              ))}
              {/* Tooltip arrow */}
              <div className="absolute top-full left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-800"></div>
            </div>
          </div>,
          document.body
        )}
      </div>



              {/* Timestamp and Read Receipt */}
        {showTimestamp && (
          <div className="flex items-center gap-1 mx-2 mt-1">
            <span className="text-xs text-gray-400">
              {formatTime(message.timestamp)}
            </span>
            {/* Read receipt indicator for own messages */}
            {isOwn && (
              <div className="flex items-center">
                {readReceipts.length > 0 ? (
                  <CheckCheck className="w-3 h-3 text-teal-500" title={`Read by ${readReceipts.length} user${readReceipts.length > 1 ? 's' : ''}`} />
                ) : (
                  <Check className="w-3 h-3 text-gray-400" title="Delivered" />
                )}
              </div>
            )}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MessageBubble;
